import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useApi } from "@/core/api";
import {
  IAccountSummary,
  IAchAccount,
  IInternalTransaction,
  SetAchAccountParams,
} from "./InternalTransactions";

export const internalTransactionsKey = (businessId?: string | null) =>
  ["internal-transactions", businessId] as const;
export const accountSummaryKey = (businessId?: string | null) =>
  ["account-summary", businessId] as const;
export const achAccountKey = (businessId?: string | null) =>
  ["ach-account", businessId] as const;

export interface DepositWithdrawParams {
  amount: number;
}

export const useDepositWithdraw = (
  isDeposit: boolean,
  businessId?: string | null,
) => {
  const queryClient = useQueryClient();
  const api = useApi();
  const pathname = isDeposit ? "deposits" : "withdrawals";

  return useMutation({
    mutationKey: ["deposit-withdraw", businessId, pathname],
    mutationFn: async ({ amount }: DepositWithdrawParams) => {
      const { data } = await api.post(`/dots/${pathname}`, {
        business_id: businessId,
        amount,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["internal-transactions", businessId],
      });
    },
  });
};

export const useGetInternalTransactions = (businessId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: internalTransactionsKey(businessId),
    queryFn: (): Promise<IInternalTransaction[]> =>
      api
        .get(`/dots/funding`, {
          params: { business_id: businessId },
        })
        .then((res) => res.data),
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 500) {
        return false;
      }
      return failureCount < 3;
    },
    enabled: !!businessId,
  });
};

export const useGetAccountSummary = (businessId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: accountSummaryKey(businessId),
    queryFn: (): Promise<IAccountSummary> =>
      api
        .get(`/dots/app_details?business_id=${businessId}`)
        .then(({ data }) => data),
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        return false;
      }
      return failureCount < 3;
    },
    enabled: !!businessId,
  });
};

export const useGetAchAccount = (businessId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: achAccountKey(businessId),
    queryFn: (): Promise<IAchAccount> =>
      api
        .get(`/dots/ach_accounts?business_id=${businessId}`)
        .then(({ data }) => data[0]),
    enabled: !!businessId,
    retry: false,
  });
};

export const useSetAchAccount = (businessId?: string | null) => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: (achAccount: SetAchAccountParams) =>
      api.post("/dots/ach_accounts", achAccount, {
        params: { business_id: businessId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: achAccountKey(businessId) });
    },
  });
};

export interface UpdateAppSettingsParams {
  auto_refill_active: boolean;
  refill_threshold?: number;
  refill_amount?: number;
}

export const useUpdateAppSettings = (businessId?: string | null) => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: (settings: UpdateAppSettingsParams) =>
      api.post("/dots/change_settings", settings, {
        params: { business_id: businessId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountSummaryKey(businessId),
      });
    },
  });
};
